/**
 * OPUS ci: al entrar a «Contexto de proyecto» se leen los documentos para llenar la pantalla.
 *
 * En ci no existe la pestaña «Resumen», pero el contexto (tipo de obra, ubicación, clima,
 * altitud…) se infiere del MISMO análisis con IA que en ai. Aquí solo se DECIDE si conviene
 * arrancarlo solo; la ejecución reutiliza `POST /summary/regenerate`, que ya trae sus
 * guardias (saldo antes, un solo análisis a la vez, reporte de consumo).
 *
 * No cuenta como documento legible un plano de CAD/BIM (IFC, DWG, DXF): no tiene texto que
 * leer y no se gasta IA en ellos.
 */

export interface ArchivoParaAnalisis {
  originalName: string;
  parseStatus: 'pending' | 'parsing' | 'ok' | 'failed' | 'skipped';
}

export type DecisionAnalisis =
  | 'analizar' // arrancarlo ahora
  | 'esperar' // aún se leen los documentos: reintentar cuando terminen
  | 'ya_hay_analisis' // no hace falta
  | 'en_curso' // ya está corriendo: solo mostrar el avance
  | 'sin_documentos'; // nada legible o no aplica: no se gasta IA

const EXT_SIN_TEXTO = /\.(ifc|dwg|dxf)$/i;

export function esDocumentoLegible(f: ArchivoParaAnalisis): boolean {
  return f.parseStatus === 'ok' && !EXT_SIN_TEXTO.test(f.originalName);
}

export function decidirAnalisisAlEntrar(args: {
  esCi: boolean;
  esEjemplo?: boolean;
  tieneAnalisis: boolean;
  estadoResumen: 'idle' | 'analyzing' | 'ready' | 'failed';
  /** Fragmentos de texto indexados (0 = nada que leer). */
  chunksTotal: number;
  archivos: ArchivoParaAnalisis[];
}): DecisionAnalisis {
  if (args.tieneAnalisis) return 'ya_hay_analisis';
  if (args.estadoResumen === 'analyzing') return 'en_curso';
  // En ai se conserva el botón manual; el ejemplo es de solo lectura: nunca se le gasta IA.
  if (!args.esCi || args.esEjemplo) return 'sin_documentos';
  if (args.archivos.some((f) => f.parseStatus === 'pending' || f.parseStatus === 'parsing')) {
    return 'esperar';
  }
  if (!args.archivos.some(esDocumentoLegible) || args.chunksTotal <= 0) return 'sin_documentos';
  return 'analizar';
}
